import { getNetworkCapabilities } from './network-capabilities';
import {
  AppUiVariant,
  Board,
  HpLevel,
  Inverter,
  SpotPriceLevel,
  OemDistribution,
  License,
  HeatWater,
  SpotPricePeriod,
  SpotRegion,
  Currency,
  Wallbox,
  SmartMeterStatus,
  HeatWaterStatus,
  PhotovoltaicStatus,
  HeatPumpStatus,
  PlugStatus,
  WallboxStatus,
  SpotBatteryStatus,
  SpotHeatPumpStatus,
  SpotHeatWaterStatus,
  SpotSmartMeterStatus,
} from './enums';

// Local calendar date, matching how the client derives "today" and "tomorrow"
// (date-fns formatISO on a local Date). toISOString() would give the UTC date,
// which is the previous day between midnight and 02:00 CEST — enough to make the
// spot page treat today's prices as some other day's.
const toIsoDate = (date: Date) =>
  [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-');

const isoToday = toIsoDate(new Date());

// setDate() rolls the month/year over for us and stays on the local calendar.
const isoTomorrow = toIsoDate(
  (() => {
    const date = new Date();
    date.setDate(date.getDate() + 1);
    return date;
  })()
);

// Low-tariff (HDO) windows the demo device is wired for, as seconds from
// midnight: high tariff during the 7:00-9:00 morning peak, low tariff for the
// rest of the day. Same shape the store holds them in below.
const hdoOn = [true, true, false, false, false, false];
const hdoTimeStart = [0, 9 * 3600, 0, 0, 0, 0];
const hdoTimeStop = [7 * 3600, 24 * 3600, 0, 0, 0, 0];

const priceDistributionLow = 1.0;
const priceDistributionHi = 1.5;

// Distribution surcharge for the period starting at `seconds`: the low rate
// inside an HDO window, the high rate outside it. Mirrors getDistribution() in
// modules/spot/spot-prices.ts, which a real device re-runs on every price fetch.
// The demo store never runs that code, so the with-distribution arrays are
// derived from the base prices here instead of being written out by hand - the
// hand-written 15M pair had drifted into being identical to the base prices, so
// the distribution-only chart view showed a flat zero for today.
const distributionAt = (seconds: number) =>
  hdoOn.some(
    (on, i) => on && seconds >= hdoTimeStart[i] && seconds < hdoTimeStop[i]
  )
    ? priceDistributionLow
    : priceDistributionHi;

const round = (value: number) => Math.round(value * 1e7) / 1e7;

const hourlyDistribution = Array.from({ length: 24 }, (_, hour) =>
  distributionAt(hour * 3600)
);

const withDistribution60M = (prices: number[]) =>
  prices.map((price, hour) => round(price + distributionAt(hour * 3600)));

const withDistribution15M = (prices: number[]) =>
  prices.map((price, period) => round(price + distributionAt(period * 900)));

const spotHoursPrice = [
  2.64, 2.53, 2.28, 1.27, 2.24, 2.46, 2.84, 3.71, 3.58, 2.34, 1.98, 1.2, 0.4,
  -0.84, 1.11, 1.78, 2.02, 2.43, 3.34, 5.42, 6.53, 4.59, 3.33, 2.72,
];

const spot15MPrice = [
  2.4257244, 2.2290572, 2.1122252, 2.1351048, 2.313517, 2.2217552, 2.0732812,
  2.0163256, 2.109061, 2.0842342, 2.1258556, 2.1993624, 2.2176174, 2.1993624,
  2.1993624, 2.1755092, 2.183298, 2.2463386, 2.274573, 2.2801712, 2.1063836,
  2.2059342, 2.3680386, 2.7470124, 2.3349362, 2.907413, 3.3117004, 4.1808818,
  4.111756, 4.81932, 4.570078, 4.003443, 4.608779, 3.703331, 2.818572, 2.223459,
  2.9814066, 2.7197516, 2.2353856, 1.8028638, 2.4763516, 2.431566, 2.0856946,
  1.891218, 2.2585086, 2.0501582, 1.807245, 1.6702108, 1.7758464, 1.6276158,
  1.6159326, 1.6096042, 1.4720832, 1.529769, 1.5716338, 1.6105778, 1.5777188,
  1.7096416, 1.8907312, 2.0613546, 1.578449, 1.8907312, 2.1896264, 2.488765,
  1.9050918, 1.9856572, 2.3181416, 2.5552132, 1.9737306, 2.6644998, 3.3192458,
  4.1969462, 3.1318278, 3.672906, 4.3478542, 5.69556, 5.6811994, 5.2394284,
  4.1368264, 3.2674016, 3.7644244, 3.2231028, 2.7720826, 2.5364714, 2.7910678,
  2.6099782, 2.2923412, 2.2156702, 2.7075816, 2.5243014, 2.3996806, 2.0942136,
  2.6075442, 2.356112, 1.9467132, 1.6828676,
];

// Tomorrow gets its own curve so stepping a day forward shows something other
// than a copy of today. Real CZ day-ahead prices (2026-04-30), taken from the
// newest spot_summaries snapshot in demo/db.sqlite — which the demo API shifts
// onto tomorrow's date anyway, so the two agree. Each hourly value is the mean
// of that hour's four quarters, as it is on a real device.
const spotHoursPriceTomorrow = [
  2.6259698, 2.5725776, 2.5264994, 2.5694082, 2.6822876, 2.9363272, 3.2210856,
  2.975579, 2.4667684, 1.1582938, 0.0058512, -0.06095, -0.3283986, -0.671669,
  -0.513199, -0.1969904, 0.0446154, 0.9725182, 2.5216234, 3.5438768, 4.1572776,
  3.208408, 2.9480296, 2.720808,
];

const spot15MPriceTomorrow = [
  2.7386054, 2.5954948, 2.5993956, 2.5701396, 2.5606314, 2.5481976, 2.5618504,
  2.619631, 2.592813, 2.506264, 2.4991938, 2.5077268, 2.5462472, 2.558681,
  2.565995, 2.6067096, 2.5384456, 2.6191434, 2.7515268, 2.819547, 2.7376302,
  2.8497782, 2.9994714, 3.1581852, 2.8363692, 3.4044232, 3.4568402, 3.1869536,
  3.588736, 3.1445324, 2.8100388, 2.3590088, 3.2630192, 2.9614386, 2.3967978,
  1.2455742, 2.7859026, 1.4893742, 0.3574108, 0.0002438, 0.0485162, 0, 0,
  -0.0251114, 0.0019504, -0.0114586, -0.0636318, -0.1704162, -0.148718,
  -0.2918286, -0.3647248, -0.5085668, -0.5468434, -0.6307106, -0.7282306,
  -0.7811352, -0.6597228, -0.530265, -0.4880876, -0.3744768, -0.3530224,
  -0.248676, -0.1531064, -0.0326692, -0.0970324, 0.008533, 0.035351, 0.2318538,
  -0.0102396, 0.1479866, 1.4498786, 2.3024472, 1.1585376, 2.490417, 2.9507114,
  3.4865838, 2.8076008, 3.1467266, 3.6921072, 4.5290726, 4.2972188, 4.423751,
  4.1740998, 3.7340408, 4.0912078, 3.3785804, 2.8453898, 2.5186978, 3.3110478,
  3.0123928, 2.7898034, 2.6783868, 2.9173108, 2.7651796, 2.622069, 2.5784288,
];

export const demoInitialStore = {
  DEVICE_ID: 'DEMO',
  BOARD: Board.V2,
  // Derived from the board the same way store/init.ts does it, so the demo can
  // never drift out of sync with a real v2 device. Settings reads
  // NETWORK_CAPABILITIES.managedNetwork unguarded, so leaving it out crashes
  // the whole /nastaveni page.
  NETWORK_CAPABILITIES: getNetworkCapabilities(Board.V2),
  BOARD_NETWORK: true,
  BOARD_RS485: true,
  BOARD_RS485_HOST: 'localhost',
  BOARD_RS485_PORT: 502,
  VERSION: {},

  SERVICE_ACTIVE: false,
  SERVICE_PASSWORD: '',

  APP_UI_VARIANT: AppUiVariant.Default,

  EM_ENERGY_CONSUMED_TOTAL: 2950.1,
  EM_ENERGY_CONSUMED_TODAY: 0.3,
  EM_ENERGY_CONSUMED_YEAR: 235.1,

  EM_ENERGY_OVERFLOW_TOTAL: 291.0,
  EM_ENERGY_OVERFLOW_TODAY: 0,
  EM_ENERGY_OVERFLOW_YEAR: 12.5,
  EM_INFO_Consumption: [0.8, 1.2, 0.3],

  HW_ON: true,
  HW_INSTALL: true,
  HW_TEMP_ENABLED: true,
  HW_CAPACITY: 250,
  HW_TEMP: 45.2,
  HW_SET_TEMP: 20,
  HW_MAX_TEMP_EK: 65,
  HW_SPOT_TEMP_EK: 55,
  HW_HYST: 4,
  PHASE_3_FVE: true,
  HW_EK_NOMINAL: 1,
  HW_ACTUAL_POWER: 0,
  HW_BATTERY_PRIO: true,
  //HW_MAX_
  HW_ERR: false,
  HW_ERR_RESET_TIME: 5400000,
  HW_ERR_RESET: false,
  ERR_RESET: false,
  com_index: 0,

  HW_INFO: {
    Status: HeatWaterStatus.PVHeating,
    Err: false,
    Err_info: '',
    Surplus: true,
    Consumption: [0.8, 1.2, 0.3],
  },

  PV_ENERGY_PRODUCED_TOTAL: 9537,
  PV_ENERGY_PRODUCED_TODAY: 42.1,
  PV_ENERGY_PRODUCED_YEAR: 3729,

  EM_ACTUAL_LOAD: [0, 0, 0],
  EM_ACTUAL_LOAD_MAX: 0,
  EM_LOAD_MAX: 90,
  EM_MAX_CURRENT: 25,
  EM_INFO: {
    Status: SmartMeterStatus.Consumption,
    Err: false,
    Err_info: '',
    Surplus: false,
    Consumption: [0.1, 0, 0],
  },

  HOME_ACTUAL_POWER: 1.8,
  PV_NOMINAL: 10, //Reatin
  PV_BAT_NOMINAL: 10, //Reatin
  PV_INSTALL: true, //Reatin
  PV_BATTERY: true, //Reatin
  PV_INVERTER_TYPE: Inverter.GoodWe,
  PV_EM_OFFSET: -1, //Reatin
  PV_OVERFLOW: true, //Reatin
  PV_ACTUAL_POWER: 9.8,
  PV_ACTUAL_POWER_STRING: [4.3, 4.5, 1.0],
  PV_ACTUAL_POWER_STRING_V: true,
  PV_SOC_MIN_FINAL: 20,
  PV_SOC_SET_MIN: 20, //Reatin
  PV_SOC_SET_UTILITY_LOST: 10, //Reatin
  PV_OFFSET_SOC_HW_PRIO: 90,
  PV_cnt_com_ok: 0,
  PV_cnt_com_err: 0,
  PV_cnt_com_write: 0,
  PV_com_ok: false,
  SOC_AUTO: true, //Reatin
  PV_ACTUAL_SOC: 91,
  PV_ACTUAL_POWER_BATTERY: -1.4,
  PV_OVERFLOW_MAX_VALUE: 0,
  UTILITY_LOSS: false,
  set_overflow: 0,
  set_SOC: 0,
  set_SOC1: 0,
  HOME_ENERGY_CONSUMED_TODAY: 5.9,
  INVERTER_STATUS: 0,

  PWM_test: 0,
  PWM_f: 5,
  PWM_pulse_time: 10,
  PWM_pulse_time_real: 0,

  PV_INFO: {
    Status: PhotovoltaicStatus.Production,
    Err: false,
    Err_info: '',
    Surplus: false,
    Consumption: [0, 0, 0],
  },

  PV_POWER_GRAPH: {
    Day: [0, 0],
    Week: [0, 0],
    Year: [0, 0],
    Total: 0,
    Last_Total: 0,
  },
  HW_POWER_GRAPH: {
    Day: [0, 0],
    Week: [0, 0],
    Year: [0, 0],
    Total: 0,
    Last_Total: 0,
  },
  EM_CONSUMED_GRAPH: {
    Day: [0, 0],
    Week: [0, 0],
    Year: [0, 0],
    Total: 0,
    Last_Total: 0,
  },
  EM_OVERFLOW_GRAPH: {
    Day: [0, 0],
    Week: [0, 0],
    Year: [0, 0],
    Total: 0,
    Last_Total: 0,
  },
  HP_CONSUMED_GRAPH: {
    Day: [0, 0],
    Week: [0, 0],
    Year: [0, 0],
    Total: 0,
    Last_Total: 0,
  },

  TEMP: [],
  TEMP_IDS: [],

  HP_INFO: {
    Status: HeatPumpStatus.Heating,
    Err: false,
    Err_info: '',
    Surplus: false,
    Consumption: [0, 0, 0],
  },

  HP_NOMINAL: 5,
  HP_PV_STABILITY: 40,
  HP_FLEXIBILITY_ON: false,
  HP_FLEXIBILITY: false,
  HP_PRIORITY: true,
  HP_ENERGY_CONSUMED_YEAR: 995,
  HP_ENERGY_CONSUMED_TODAY: 7.1,
  HP_ENERGY_CONSUMED_TOTAL: 2019,
  HP_ECOONE_API_IP: '',
  HP_cnt_com_ok: 0,
  HP_cnt_com_err: 0,
  HP_com_ok: true,
  HP_ACTUAL_POWER: 2.8,
  HP_IN: 0,
  HP_ENABLED: true,
  HP_TYPE: 1,
  HP_TEMP: 43.1,
  OUTDOOR_TEMP: 11.2,
  HW_SOC_STOP: 20,
  HW_SOC_STOP_UTILITY: 80,
  HP_EQUITHERMAL_CORRECTION: 0.5,
  HP_INFO_Status: '',
  HP_FLEXI_ROOM: true,
  HP_FLEXI_HW: true,
  HP_FLEXI_COOL: true,
  HP_FLEXI_AN: true,

  HP_ROOM_TEMP: 21.3,
  HP_ROOM_ON: true,
  HP_ROOM_TEMP_V: true,
  HP_ROOM_SET_TEMP_MIN: 21,
  HP_ROOM_SET_TEMP_MAX: 22,
  HP_ROOM_SET_FLEXI_TEMP: 0.8,
  HP_HW_TEMP: 45.1,
  HP_HW_ON: true,
  HP_HW_TEMP_V: true,
  HP_HW_SET_TEMP: 45,
  HP_HW_SET_FLEXI_TEMP: 5,

  AT_H: 0,

  uptime: 0,

  RELAY_INSTALL: true,
  RELAY_ENABLED: [true, true, true, true],
  RELAY_NAME: ['Relé 1', 'Relé 2', 'Relé 3', 'Relé 4'],
  RELAY_CONTROL_TYPE: [
    ['spot-hi', 'hdo', 'plug2', 'plug3'],
    ['none', 'none', 'none', 'none'],
  ],
  RELAY_OUTPUT: [true, false, true, false],
  RELAY_USER_ADVANCED_CONTROL: true,

  SP_INSTALL: true,
  SP_ON: true,
  SP_USER_ADVANCED_CONTROL: true,
  SP_DISCHARGE_TO_GRID_SER: true,
  SP_DISCHARGE_TO_GRID: true,
  SP_OVERFLOW_OPTIMALIZATION: true,
  SP_HOURS_PRICE: [...spotHoursPrice],
  SP_HOURS_PRICE_DATE: isoToday,
  SP_ACTUAL_PRICE: 0,
  SP_ACTUAL_PRICE_LOW: false,
  SP_ACTUAL_PRICE_HI: false,
  SP_PV_RADIATION_DATE: isoToday,
  SP_LATITUDE: 50.09,
  SP_LONGITUDE: 14.42,
  SP_LOW_PRICE: 2.0,
  SP_HI_PRICE: 2.8,
  SP_PV_RADIATION: [0, 0],
  SP_FORECAST_PV: 65,
  SP_FORECAST_HOME_CONSUMPTIN: 31,
  SP_FORECAST_HOME_CONSUMPTIN_PV: 17,
  SP_FORECAST_BATTERY_CONSUMPTIN: 7.8,
  SP_FORECAST_HW_CONSUMPTIN: 7.1,
  SP_FORECAST_HP_CONSUMPTIN: 13.2,
  SP_FORECAST_PV_DEBREEFING: 1,
  SP_HP_STATUS: SpotHeatPumpStatus.FlexibilitySpot,
  SP_HW_STATUS: SpotHeatWaterStatus.FlexibilityPV,
  SP_BAT_STATUS: SpotBatteryStatus.FlexibilityPV,
  SP_EM_STATUS: SpotSmartMeterStatus.OverflowFlexibilitySell,
  SP_HP: false,
  SP_HW: false,
  SP_BAT: false,
  SP_EM: false,
  SP_HP_NOW_INCREASE: false,
  SP_HP_NOW_REDUCE: false,
  PV_BATTERY_CONTROL: true,
  PV_OVERFLOW_CONTROL: true,
  SP_BATTERY_CONTROL_SERVICE: true,
  SP_BATTERY_CONTROL_CUSTOMER: true,
  SP_BATTERY_STOP_DISCHARGE_LOW_PRICE: true,
  SP_BATTERY_STOP_DISCHARGE_MIDDLE_PRICE: false,
  SP_PRICE_ONLY_VIEW: true,
  SP_HOURS_PRICE_DISTRIBUTION: hourlyDistribution,
  SP_HOURS_PRICE_WITH_DISTRIBUTION: withDistribution60M(spotHoursPrice),

  shelly_cnt_com_ok: [0, 0, 0],
  shelly_cnt_com_err: [0, 0, 0],
  shelly_com_ok: [false, false, false],
  PL_IP: ['100.100.1.51', '100.100.1.52', '100.100.1.53'],
  PL_ON: [true, false, false],
  PL_INSTALL: true,
  PL_INSTALL_I: [true, false, false],
  PL_CONSUMPTIN: [0.2, 0, 0],
  PL_NUMBER: 1,
  PL_ENERGY_CONSUMPTIN_TODAY: [1.1, 0, 0],
  PL_ENERGY_CONSUMPTIN_YEAR: [7.12, 0, 0],
  PL_BAT: [true, false, false],
  PL_BAT_START: [90, 90, 90],
  PL_BAT_STOP: [80, 80, 80],
  PL_SPOT: [false, false, false],
  PL_SPOT_LOW: [false, false, false],
  PL_SPOT_HI: [false, false, false],
  PL_SPOT_MI: [false, false, false],
  PL_TIME: [false, false, false],
  PL_TIME_START: [0, 0, 0],
  PL_TIME_STOP: [0, 0, 0],
  PL_OVERFLOW: [false, true, true],
  PL_OVERFLOW_START_POWER: [-3, -6, -6],
  PL_OVERFLOW_STOP_POWER: [0.1, -3.1, -3.1],
  PL_OVERFLOW_TON: [60, 120, 180],
  PL_OVERFLOW_CONSUMPTION: [3.0, 2.8, 2.8],
  PL_STATUSLED: true,
  PL_LED: false,
  PL_INFO: [
    {
      Status: PlugStatus.BatteryControl,
      Err: false,
      Err_info: '',
      Surplus: false,
      Consumption: [0, 0, 0],
    },
    {
      Status: PlugStatus.NotActive,
      Err: false,
      Err_info: '',
      Surplus: false,
      Consumption: [0, 0, 0],
    },
    {
      Status: PlugStatus.NotActive,
      Err: false,
      Err_info: '',
      Surplus: false,
      Consumption: [0, 0, 0],
    },
  ],

  //Animation value
  anim_EM_Consumption: [0.1, 0, 0],
  anim_PV_ACTUAL_POWER: 9.8,
  anim_HW_ACTUAL_POWER: 2.3,
  anim_PV_ACTUAL_POWER_BATTERY: -1.4,
  anim_HOME_ACTUAL_POWER: 1,
  anim_HP_ACTUAL_POWER: 1.8,
  animation_time_response: 3000,
  anim_WB_ACTUAL_POWER: 4.2,
  test_persisted: false,
  test_hours: 0,

  MODBUS_TEST_ADDRESS: '100.100.1.100',
  MODBUS_TEST_PORT: 502,
  CUSTOMER: 'Tomáš Super',

  //Err list
  ERR_LIST: ['', '', ''],

  // new statistics
  NEW_PL_ENERGY_PRODUCED_TOTAL: 0,

  HW_ENERGY_PRODUCED_TOTAL: 157.0,
  PL_ENERGY_PRODUCED_TOTAL: 0,

  WB_MAX_CURRENT_NONE_MODE: 16,
  WB_MAX_CURRENT_SPOT: 16,
  WB_MAX_CURRENT_BATTERY: 16,
  WB_MAX_CURRENT: 8,
  WB_IP_ADRESS: '100.100.1.120',
  WB_IP_PORT: 8000,
  WB_INSTALL: true,
  WB_ON: true,
  WB_cnt_com_ok: 0,
  WB_cnt_com_err: 0,
  WB_cnt_com_write: 0,
  WB_com_ok: false,
  WB_ACTUAL_POWER: 4.2,
  WB_STATE: 1,
  WB_INSTALL_V: false,
  WB_INFO: {
    Status: WallboxStatus.Charging,
    Err: false,
    Err_info: '',
    Surplus: false,
    Consumption: [0, 0, 0],
  },
  NEW_WB_ENERGY_CONSUMPTION_TOTAL: 7351,
  STATS_WB_ENERGY_CONSUMPTION_TOTAL: 7351,
  WB_ENERGY_CONSUMPTION_YEAR: 471,
  WB_ENERGY_CONSUMPTION_TODAY: 10,
  WB_CAR_CONNECT: true,
  WB_MODE_PV: false,
  WB_MODE_SPOT: true,
  WB_MODE_AUTO: false,
  WB_MODE_NONE: false,
  WB_MIN_CHARGE_ENERGY: 5,
  WB_SPOT_MI_STOP: false,
  WB_SPOT_HI_STOP: true,
  WB_ENERGY_CONSUMPTION_NOW: 3,
  WB_BAT_STOP: 80,
  WB_BAT_START: 95,
  WB_TIME_OF_DISCONNECT: 25200,
  LICENSE_STATUS: 'SMART',
  SP_DISCHARGE_TO_GRID_MIN_PRICE: 5.0,
  SP_DISCHARGE_ALL_TO_GRID_MIN_PRICE: 10.0,
  SP_MIN_SPOT_DIFFERENCE_BATTERY_SOC: 40,
  SP_MIN_SPOT_DIFFERENCE_PRICE: 2.0,
  SP_ENERGY_FOR_SALE: 3.5,
  SP_PRICE_OFFSET_OVERFLOW: 0.5,
  PRICE_DISTRIBUTION_LOW: priceDistributionLow,
  PRICE_DISTRIBUTION_HI: priceDistributionHi,
  PRICE_DISTRIBUTION: 0.3,
  HDO_ON: hdoOn,
  HDO_TIME_START: hdoTimeStart,
  HDO_TIME_STOP: hdoTimeStop,
  HDO: false,
  EM_INSTALL: true,

  SP_BATTERY_DISCHARGE_TABLE: [
    true,
    true,
    true,
    false,
    true,
    true,
    true,
    true,
    true,
    true,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
  ],
  SP_BATTERY_GRID_CHARGE_TABLE: [
    false,
    false,
    false,
    true,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
  ],
  SP_BATTERY_GRID_DISCHARGE_TABLE: [
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    true,
    false,
    false,
    false,
  ],
  SP_OVERFLOW_TABLE: [
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    false,
    false,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
  ],
  SP_HW_GRID_HEAT_TABLE: [
    false,
    false,
    false,
    true,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
  ],
  SP_HP_TABLE: [
    HpLevel.Normal,
    HpLevel.Normal,
    HpLevel.Normal,
    HpLevel.Increase,
    HpLevel.Normal,
    HpLevel.Normal,
    HpLevel.Reduction,
    HpLevel.Reduction,
    HpLevel.Reduction,
    HpLevel.Normal,
    HpLevel.Increase,
    HpLevel.Increase,
    HpLevel.Increase,
    HpLevel.Increase,
    HpLevel.Increase,
    HpLevel.Increase,
    HpLevel.Increase,
    HpLevel.Normal,
    HpLevel.Reduction,
    HpLevel.Reduction,
    HpLevel.Reduction,
    HpLevel.Reduction,
    HpLevel.Reduction,
    HpLevel.Normal,
  ],
  SP_PRICE_LEVEL: [
    SpotPriceLevel.Mid,
    SpotPriceLevel.Mid,
    SpotPriceLevel.Mid,
    SpotPriceLevel.Low,
    SpotPriceLevel.Mid,
    SpotPriceLevel.Mid,
    SpotPriceLevel.Hi,
    SpotPriceLevel.Hi,
    SpotPriceLevel.Hi,
    SpotPriceLevel.Mid,
    SpotPriceLevel.Low,
    SpotPriceLevel.Low,
    SpotPriceLevel.Low,
    SpotPriceLevel.Low,
    SpotPriceLevel.Low,
    SpotPriceLevel.Low,
    SpotPriceLevel.Low,
    SpotPriceLevel.Mid,
    SpotPriceLevel.Hi,
    SpotPriceLevel.Hi,
    SpotPriceLevel.Hi,
    SpotPriceLevel.Hi,
    SpotPriceLevel.Hi,
    SpotPriceLevel.Mid,
  ],

  STATS_EM_ENERGY_CONSUMED_TODAY: 0.3,
  STATS_EM_ENERGY_OVERFLOW_TODAY: 8.3,
  STATS_PV_ENERGY_PRODUCED_TODAY: 41.2,
  STATS_HOME_ENERGY_CONSUMED_TODAY: 5.9,
  STATS_HW_ENERGY_PRODUCED_TODAY: 10.1,
  STATS_HP_ENERGY_CONSUMED_TODAY: 7.1,
  STATS_WB_ENERGY_CONSUMPTION_TODAY: 10.0,

  STATS_EM_ENERGY_CONSUMED_YEAR: 235.1,
  STATS_EM_ENERGY_OVERFLOW_YEAR: 12.5,
  STATS_PV_ENERGY_PRODUCED_YEAR: 3729,
  STATS_HW_ENERGY_PRODUCED_YEAR: 157.0,
  STATS_HP_ENERGY_CONSUMED_YEAR: 995,
  STATS_WB_ENERGY_CONSUMPTION_YEAR: 471,

  PREMIUM: true,

  INITIAL_WIZARD_COMPLETED_AT: '2026-01-01T00:00:00.000Z',

  SPOT_WIZARD_COMPLETED_AT: '2026-01-01T00:00:00.000Z',

  INVERTER_POWER: 10,
  PV_BATTERY_POWER_PERCENT: 100,
  HW_ABOVE_TEMP_RELAY: [40, 40, 40, 40],
  HW_ABOVE_TEMP_RELAY_HYST: [4, 4, 4, 4],
  HW_BELOW_TEMP_RELAY: [40, 40, 40, 40],
  HW_BELOW_TEMP_RELAY_HYST: [4, 4, 4, 4],
  BAT_BELOW_SOC_RELAY: [20, 20, 20, 20],
  BAT_BELOW_SOC_RELAY_HYST: [5, 5, 5, 5],
  BAT_ABOVE_SOC_RELAY: [95, 95, 95, 95],
  BAT_ABOVE_SOC_RELAY_HYST: [5, 5, 5, 5],
  SPOT_PRICE_ABOVE_RELAY: [5, 5, 5, 5],
  SPOT_PRICE_BELOW_RELAY: [0, 0, 0, 0],
  SPOT_PRICE_ABOVE_RELAY_15MIN: [5, 5, 5, 5],
  SPOT_PRICE_BELOW_RELAY_15MIN: [0, 0, 0, 0],
  PV_BATTERY_CHARGING_POWER: 5,
  PV_BATTERY_DISCHARGING_POWER: 5,
  PL_SPOT_LOW_15MIN: [
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
  ],
  PL_SPOT_HI_15MIN: [
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
  ],
  PL_SPOT_MI_15MIN: [
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
  ],
  PL_SPOT_NEG_15MIN: [
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
  ],
  PL_SP_PRICE_PERIOD: [
    SpotPricePeriod['60M'],
    SpotPricePeriod['60M'],
    SpotPricePeriod['60M'],
    SpotPricePeriod['60M'],
    SpotPricePeriod['60M'],
    SpotPricePeriod['60M'],
    SpotPricePeriod['60M'],
    SpotPricePeriod['60M'],
    SpotPricePeriod['60M'],
  ],
  OEM_DISTRIBUTION: OemDistribution.Infigy,
  LICENSE: License.SMART,
  TEST_INFO_LABEL: [
    'Netestováno',
    'Netestováno',
    'Netestováno',
    'Netestováno',
    'Netestováno',
    'Netestováno',
  ],
  PRODUCT_TESTER: '',
  CUSTOMER_EMAIL: '',
  SERVICE_EMAIL: '',
  SERVICER: '',
  SP_HP_NOW_REDUCE_ON: false,
  SP_HP_NOW_INCREASE_ON: false,
  SERIAL_NUMBER: '',
  PL_LED_BAT: false,
  PL_LED_OVER: false,
  SERIAL_NUMBER_SWITCHBOARD: '',
  PL_NAME: [
    'Zásuvka 1',
    'Zásuvka 2',
    'Zásuvka 3',
    'Zásuvka 4',
    'Zásuvka 5',
    'Zásuvka 6',
    'Zásuvka 7',
    'Zásuvka 8',
    'Zásuvka 9',
  ],
  LOGIN_LIST: ['', '', ''],
  PHASE_3_HW: true,
  HW_LEGIONELLA_ON: false,
  HW_LEGIONELLA_TEMP: 60,
  HW_LEGIONELLA_LAST_TIME: '2026-01-01T00:00:00.000Z',
  HP_HW_MAX_SET_TEMP: 53,
  PL_SHELLY_HW_PLUG: false,
  INVERTER_CONFIG: {},
  INVERTER_STATS_PERSISTED: {},
  WB_CLIENT_ID: 1,
  WB_TYPE: Wallbox.Solax,
  WB_ACTUAL_POWER_TOTAL: 0,
  SP_PRICE_BELOW_PLUG_VALUE: [0, 0, 0, 0, 0, 0, 0, 0, 0],
  SP_PRICE_ABOVE_PLUG_VALUE: [0, 0, 0, 0, 0, 0, 0, 0, 0],
  SP_PRICE_BELOW_PLUG_15MIN_VALUE: [0, 0, 0, 0, 0, 0, 0, 0, 0],
  SP_PRICE_ABOVE_PLUG_15MIN_VALUE: [0, 0, 0, 0, 0, 0, 0, 0, 0],
  WB_ENERGY_CONSUMPTION_TOTAL: 0,
  EM_METERS_CONFIG: {},
  WIND_BATTERY_CHARGE_TOTAL: 0,
  HDO_MODE: 1,
  HDO_WEEKEND: false,
  HDO_ON_WEEKEND: [false, false, false, false, false, false],
  HDO_TIME_START_WEEKEND: [0, 0, 0, 0, 0, 0],
  HDO_TIME_STOP_WEEKEND: [0, 0, 0, 0, 0, 0],
  HW_HDO: false,
  SP_CHARGE_BATTERY_FINAL_SOC_MAX: 95,
  DELTA_GREEN_API_ENABLED: false,
  DELTA_GREEN_BATTERY_MAX_POWER_IN: 0,
  DELTA_GREEN_BATTERY_MAX_POWER_OUT: 0,
  DELTA_GREEN_PV_ORIENTATION: 0,
  WIND_INSTALL: false,
  WIND_PRODUCED_TOTAL: 0,
  WIND_PRODUCED_TODAY: 0,
  WIND_PRODUCED_YEAR: 0,
  HW_CONTROL_TYPE: HeatWater.Proportional,
  PV_SOC_SET_UTILITY_LOST_V: true,
  PV_STRING_NUMBER: 2,
  PL_MORE_PLUGS: false,
  PL_MORE_PLUGS_NUMBER: 3,
  SP_HW_K: 1.5,
  HW_ERR_POWER_ENABLED: true,
  WB_MODE_PV2: false,
  WB_MODE_PV2_3A: true,
  WB_MODE_PV2_6A: true,
  HP_ROOM_COOL_ON: false,
  EAN_PURCHASE: 0,
  PV_SOLAX_EXTERNAL_PV: false,
  PV_SOFAR_EXTERNAL_PV: false,
  SP_BATTERY_STOP_DISCHARGE_AUTO: false,
  SP_BATTERY_STOP_DISCHARGE_AUTO_DEV: false,
  HW_SPOT_HI_STOP: false,
  HW_SPOT_MI_STOP: false,
  SP_BATTERY_K: 1.0,
  APP_UI_CUSTOM_TABS: [],
  HP_SOC_STOP: 30,
  MQTT_CLOUD_ENABLED: false,
  MQTT_CLOUD_DEVICES: [],
  MQTT_BROKER_ENABLED: false,
  PLC_CONFIG: {
    enabled: false,
    ipAddress: '192.168.100.100',
    username: 'user',
    password: 'pass',
    pollInterval: 1000,
    gridMetrics: { enabled: false, direction: 'read' },
    pvMetrics: { enabled: false, direction: 'read' },
    kgjMetrics: { enabled: false, direction: 'disabled' },
  },
  APP_MESSAGES_DEV: false,
  CLOUD_PLUGS_BATTERY_CONTROLLED: false,
  CLOUD_PLUGS_BATTERY_START: 90,
  CLOUD_PLUGS_BATTERY_STOP: 70,
  CLOUD_PLUGS_TIME_CONTROLLED: false,
  CLOUD_PLUGS_TIME_START: 0,
  CLOUD_PLUGS_TIME_STOP: 0,
  CLOUD_PLUGS_OVERFLOW_CONTROLLED: false,
  CLOUD_PLUGS_OVERFLOW_START_POWER: -0.5,
  CLOUD_PLUGS_OVERFLOW_STOP_POWER: 0.1,
  CLOUD_PLUGS_PV_POWER_CONTROLLED: false,
  CLOUD_PLUGS_PV_POWER_START_POWER: 1.5,
  CLOUD_PLUGS_PV_POWER_STOP_POWER: 0.5,
  CLOUD_PLUGS_CONTROL_CONDITION: 'or',
  CLOUD_PLUGS_LED_MODE_SWITCH: true,
  CLOUD_PLUGS_LED_MODE_BATTERY: false,
  CLOUD_PLUGS_LED_MODE_OVERFLOW: false,
  PL_SPOT_NEG: [false, false, false, false, false, false, false, false, false],
  PL_SPOT_BELOW_PLUG: [
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
  ],
  PL_SPOT_ABOVE_PLUG: [
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
  ],
  PL_SPOT_BELOW_PLUG_15MIN: [
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
  ],
  PL_SPOT_ABOVE_PLUG_15MIN: [
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
  ],
  THERMOSTAT_INSTALL: false,
  NUMBER_OF_THERMOSTATS: 1,
  THERMOSTAT_SETTINGS: [
    {
      setTemp: 20,
      flexibilityTemp: 0.5,
      pvFlexibilityActiveSet: false,
      spFlexibilityActiveUpSet: false,
      spFlexibilityActiveDownSet: false,
      on: false,
      name: '',
    },
  ],
  THERMOSTAT_HYSTERESIS: 0.5,
  THERMOSTAT_HP_CONSUMPTION: 4,
  THERMOSTAT_HP_BLOCKED: false,
  WB_MAX_CURRENT_BATTERY_AUTO: false,
  WB_HP_PRIORITY: false,
  HP_SOC_STOP_UTILITY: 0,
  SP_PRICE_PERIOD: SpotPricePeriod['15M'],
  SP_HOURS_PRICE_TOMORROW: [...spotHoursPriceTomorrow],
  SP_HOURS_PRICE_WITH_DISTRIBUTION_TOMORROW: withDistribution60M(
    spotHoursPriceTomorrow
  ),
  SP_HOURS_PRICE_TOMORROW_DATE: isoTomorrow,
  SP_15M_PRICE: [...spot15MPrice],
  SP_15M_PRICE_TOMORROW: [...spot15MPriceTomorrow],
  SP_15M_PRICE_WITH_DISTRIBUTION_TOMORROW:
    withDistribution15M(spot15MPriceTomorrow),
  SP_15M_PRICE_TOMORROW_DATE: isoTomorrow,
  HW_SHELLIES: false,
  HW_SHELLIES_ID: '',
  PV_ACTIVE_POWER_CONTROL: false,
  PV_ACTIVE_POWER_NOMINAL: 5,
  TOWN_CODE: 554782,
  BEZ_DODAVATELE_API_ENABLED: false,
  OFFSET_FOR_NOT_OVERFLOW: 90,
  OFFSET_FOR_OVERFLOW: 30,
  DEV_GOODWE_NEXT_VISIBLE: false,
  DEV_SOLAX_NEXT_VISIBLE: false,
  DEV_VERTO_VISIBLE: false,
  LEGACY_INVERTERS: false,
  DEV_HOME_CONSUMPTION_GRAPH: false,
  NOTIF_SPOT_NEGATIVE_ENABLE: false,
  NOTIF_UTILITY_LOSS_ENABLE: false,
  NOTIF_PV_PRODUCTION_MWH_ENABLE: false,
  NOTIF_INVERTER_DISCONNECTED_ENABLE: false,
  NOTIF_HEAT_WATER_MAX_TEMP_ENABLE: false,
  NOTIF_WALLBOX_CHARGING_ENABLE: false,
  NOTIF_LIMIT_HOME_ENABLE: false,
  NOTIF_LIMIT_HOME_VALUE: 10,
  NOTIF_LIMIT_HEAT_WATER_ENABLE: false,
  NOTIF_LIMIT_HEAT_WATER_VALUE: 6,
  NOTIF_LIMIT_HEAT_PUMP_ENABLE: false,
  NOTIF_LIMIT_HEAT_PUMP_VALUE: 10,
  NOTIF_LIMIT_WALLBOX_ENABLE: false,
  NOTIF_LIMIT_WALLBOX_VALUE: 10,
  NOTIF_SPOT_DISCHARGING_BATTERY_ENABLE: false,
  NOTIF_SPOT_CHARGING_BATTERY_ENABLE: false,
  NOTIF_SPOT_DAILY_SUMMARY_ENABLE: false,
  NOTIF_SPOT_DAILY_PREDICTION_ENABLE: false,
  NOTIF_BATTERY_MAX_SOC_ENABLE: false,
  NOTIF_BATTERY_MAX_SOC_VALUE: 90,
  NOTIF_BATTERY_MIN_SOC_ENABLE: false,
  NOTIF_BATTERY_MIN_SOC_VALUE: 10,
  PREMIUM_BANNER_HIDDEN_PERMANENTLY: false,
  PREMIUM_BANNER_HIDDEN_AT: undefined,
  DEV_PREMIUM_OVERRIDE: undefined,
  OFFSET_ON_GRID: 10,
  WB_MODE_NONE_HDO: false,
  SP_15M_PRICE_WITH_DISTRIBUTION: withDistribution15M(spot15MPrice),
  SP_PRICE_HYSTERESIS: 0.15,
  SOLIDSUN_KZ_API_ENABLED: false,
  SOLIDSUN_OP_NUMBER: '',
  SP_REGION: SpotRegion.CZ,
  CURRENCY: Currency.CZK,
  PV_OFFSET_SOC_HP_PRIO: 85,

  INITIAL_WIZARD_BANNER_HIDDEN_PERMANENTLY: false,
  EMS: false,
  PV_INSTALL_V1: true,
  SP_INSTALL_V1: true,
  PL_INSTALL_V1: true,
  HW_INSTALL_V1: true,
  WB_INSTALL_V1: true,
  HP_ENABLED_V1: true,

  // 15M battery discharge table (96 quarters) - discharge blocked at Low price (SP_BATTERY_STOP_DISCHARGE_LOW_PRICE=true)
  SP_BATTERY_DISCHARGE_TABLE_15M: [
    // 00:00-05:59 (indices 0-23) - Mid prices, discharge allowed
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    // 06:00-08:59 (indices 24-35) - Hi prices, discharge allowed
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    // 09:00-09:59 (indices 36-39) - Mixed, slot 39 is Low
    true,
    true,
    true,
    false,
    // 10:00-11:29 (indices 40-45) - Mid prices
    true,
    true,
    true,
    true,
    true,
    true,
    // 11:30-14:59 (indices 46-59) - Low prices, discharge blocked
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    // 15:00-15:29 (indices 60-61) - Low still
    false,
    false,
    // 15:30-17:14 (indices 62-68) - Mid prices, discharge allowed
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    // 17:15-20:44 (indices 69-82) - Hi prices, discharge allowed
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    // 20:45-23:44 (indices 83-94) - Mid prices, discharge allowed
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    // 23:45-23:59 (index 95) - Low
    false,
  ],

  // 15M battery grid charge table (96 quarters) - cheapest window ~11:45-14:15
  SP_BATTERY_GRID_CHARGE_TABLE_15M: [
    // 00:00-11:44 (indices 0-46) - not in charge window
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    // 11:45-14:00 (indices 47-56) - cheapest window, charge from grid
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    // 14:15-23:59 (indices 57-95)
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
  ],

  // 15M battery grid discharge table (96 quarters) - most expensive window after 15:00, only if price > 5.0
  SP_BATTERY_GRID_DISCHARGE_TABLE_15M: [
    // 00:00-18:44 (indices 0-74) - not in discharge window or price too low
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    // 18:45-19:29 (indices 75-77) - peak prices >5 CZK, discharge to grid
    true,
    true,
    true,
    // 19:30-23:59 (indices 78-95)
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
  ],

  // 15M overflow table (96 quarters) - all prices above SP_PRICE_OFFSET_OVERFLOW (0.5 CZK)
  SP_OVERFLOW_15M_TABLE: [
    // All 15M prices are ≥1.47 CZK, well above 0.5 threshold → always allow overflow
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
  ],

  // 15M hot water grid heat table (96 quarters) - cheapest 8 quarters before 19:00
  SP_HW_GRID_HEAT_TABLE_15M: [
    // 00:00-12:29 (indices 0-49) - not in cheapest slots
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    // 12:30-14:00 (indices 50-56) - cheapest slots for HW heating
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    // 14:15 (index 57)
    false,
    false,
    false,
    // 15:00 (index 60) - another cheap slot
    true,
    // 15:15-23:59 (indices 61-95) - not cheapest
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
  ],

  // 15M price level table (96 quarters) - with hysteresis (Low<1.85, Mid, Hi>2.95)
  SP_PRICE_15M_LEVEL: [
    // 00:00-05:59 (indices 0-23) - Mid range prices ~2.0-2.3
    SpotPriceLevel.Mid,
    SpotPriceLevel.Mid,
    SpotPriceLevel.Mid,
    SpotPriceLevel.Mid,
    SpotPriceLevel.Mid,
    SpotPriceLevel.Mid,
    SpotPriceLevel.Mid,
    SpotPriceLevel.Mid,
    SpotPriceLevel.Mid,
    SpotPriceLevel.Mid,
    SpotPriceLevel.Mid,
    SpotPriceLevel.Mid,
    SpotPriceLevel.Mid,
    SpotPriceLevel.Mid,
    SpotPriceLevel.Mid,
    SpotPriceLevel.Mid,
    SpotPriceLevel.Mid,
    SpotPriceLevel.Mid,
    SpotPriceLevel.Mid,
    SpotPriceLevel.Mid,
    SpotPriceLevel.Mid,
    SpotPriceLevel.Mid,
    SpotPriceLevel.Mid,
    SpotPriceLevel.Mid,
    // 06:00-06:29 (indices 24-25) - rising, still Mid
    SpotPriceLevel.Mid,
    SpotPriceLevel.Mid,
    // 06:30-08:59 (indices 26-35) - Hi prices (3.3-4.8)
    SpotPriceLevel.Hi,
    SpotPriceLevel.Hi,
    SpotPriceLevel.Hi,
    SpotPriceLevel.Hi,
    SpotPriceLevel.Hi,
    SpotPriceLevel.Hi,
    SpotPriceLevel.Hi,
    SpotPriceLevel.Hi,
    SpotPriceLevel.Hi,
    SpotPriceLevel.Mid,
    // 09:00-09:44 (indices 36-38) - dropping
    SpotPriceLevel.Hi,
    SpotPriceLevel.Hi,
    SpotPriceLevel.Mid,
    // 09:45 (index 39) - 1.80, drops to Low
    SpotPriceLevel.Low,
    // 10:00-10:14 (indices 40-41) - 2.48/2.43, back to Mid
    SpotPriceLevel.Mid,
    SpotPriceLevel.Mid,
    // 10:15-10:59 (indices 42-43) - Mid
    SpotPriceLevel.Mid,
    SpotPriceLevel.Mid,
    // 11:00-11:29 (indices 44-45) - Mid
    SpotPriceLevel.Mid,
    SpotPriceLevel.Mid,
    // 11:30-14:59 (indices 46-59) - Low prices (1.47-1.81)
    SpotPriceLevel.Low,
    SpotPriceLevel.Low,
    SpotPriceLevel.Low,
    SpotPriceLevel.Low,
    SpotPriceLevel.Low,
    SpotPriceLevel.Low,
    SpotPriceLevel.Low,
    SpotPriceLevel.Low,
    SpotPriceLevel.Low,
    SpotPriceLevel.Low,
    SpotPriceLevel.Low,
    SpotPriceLevel.Low,
    SpotPriceLevel.Low,
    SpotPriceLevel.Low,
    // 15:00-15:29 (indices 60-61) - still Low (1.58, 1.89)
    SpotPriceLevel.Low,
    SpotPriceLevel.Low,
    // 15:30-17:14 (indices 62-68) - Mid (2.19-2.56)
    SpotPriceLevel.Mid,
    SpotPriceLevel.Mid,
    SpotPriceLevel.Mid,
    SpotPriceLevel.Mid,
    SpotPriceLevel.Mid,
    SpotPriceLevel.Mid,
    SpotPriceLevel.Mid,
    // 17:15-20:44 (indices 69-82) - Hi prices (2.66-5.70)
    SpotPriceLevel.Mid,
    SpotPriceLevel.Hi,
    SpotPriceLevel.Hi,
    SpotPriceLevel.Hi,
    SpotPriceLevel.Hi,
    SpotPriceLevel.Hi,
    SpotPriceLevel.Hi,
    SpotPriceLevel.Hi,
    SpotPriceLevel.Hi,
    SpotPriceLevel.Hi,
    SpotPriceLevel.Hi,
    SpotPriceLevel.Hi,
    SpotPriceLevel.Hi,
    SpotPriceLevel.Hi,
    // 20:45-23:44 (indices 83-94) - Mid prices (2.22-2.79)
    SpotPriceLevel.Mid,
    SpotPriceLevel.Mid,
    SpotPriceLevel.Mid,
    SpotPriceLevel.Mid,
    SpotPriceLevel.Mid,
    SpotPriceLevel.Mid,
    SpotPriceLevel.Mid,
    SpotPriceLevel.Mid,
    SpotPriceLevel.Mid,
    SpotPriceLevel.Mid,
    SpotPriceLevel.Mid,
    SpotPriceLevel.Mid,
    // 23:45-23:59 (index 95) - Low (1.68)
    SpotPriceLevel.Low,
  ],
};
